import React, { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { Phone, ArrowRight } from "lucide-react";
import Seo from "@/components/Seo";
import Icon from "@/components/Icon";
import { Reveal, MaskedLines } from "@/components/Reveal";
import BrandMarquee from "@/components/Marquee";
import IleDeFranceMap from "@/components/IleDeFranceMap";
import QuoteForm from "@/components/QuoteForm";
import CTASection from "@/components/CTASection";
import GoogleReviews, { GoogleTrustBar } from "@/components/GoogleReviews";
import LongForm from "@/components/LongForm";
import { SEO_CONTENT } from "@/data/seoContent";
import { CONTACT, AUDIENCES, METHODS, FAQ, SERVICES } from "@/data/site";

const HERO_IMG =
  "https://images.unsplash.com/photo-1527066236128-2ff79f7b9705?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000";

export default function Home() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "28%"]);
  const overlayY = useTransform(scrollYProgress, [0, 1], ["0%", "-14%"]);

  const schema = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    name: "FuiteX",
    description: "Recherche de fuite d'eau non destructive en Île-de-France.",
    areaServed: "Île-de-France",
    telephone: CONTACT.phoneTel,
    email: CONTACT.email,
    slogan: CONTACT.slogan,
  };

  return (
    <>
      <Seo
        title="FuiteX | Recherche de Fuite d'Eau en Île-de-France"
        description="Fuite d'eau invisible ? FuiteX la localise sans destruction : caméra thermique, détection acoustique. Intervention rapide en Île-de-France. Devis gratuit."
        canonical="/"
        schema={schema}
      />

      {/* HERO */}
      <section ref={heroRef} className="relative min-h-[100svh] flex items-end overflow-hidden bg-ink grain">
        <motion.div style={{ y: imgY }} className="absolute inset-0 -z-0">
          <img src={HERO_IMG} alt="Détection de fuite d'eau de précision" className="h-[120%] w-full object-cover opacity-40" />
          <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/80 to-ink/40" />
        </motion.div>

        <motion.div style={{ y: overlayY }} className="container-x relative z-10 pb-24 pt-36">
          <Reveal className="flex items-center gap-2 mb-7">
            <span className="h-2 w-2 rounded-full bg-flame animate-pulse" />
            <span className="overline text-white/70">{CONTACT.zone} · {CONTACT.signature}</span>
          </Reveal>

          <h1 className="text-4xl sm:text-6xl lg:text-7xl font-extrabold tracking-tighter text-white leading-[1.02] max-w-5xl">
            <MaskedLines
              lines={["Recherche de fuite d'eau", "non destructive en Île-de-France"]}
              lineClassName=""
            />
          </h1>

          <Reveal delay={0.5} className="mt-7 max-w-xl text-lg text-white/75">
            Voir l'invisible, trouver l'essentiel. Nous localisons votre fuite au centimètre
            près — sans casser vos murs — grâce à la caméra thermique, l'acoustique et le gaz traceur.
          </Reveal>

          <Reveal delay={0.65} className="mt-9 flex flex-col sm:flex-row gap-4">
            <Link to="/contact" data-testid="hero-devis-btn" className="btn-cta text-base px-8 py-4">
              Devis gratuit <ArrowRight size={18} />
            </Link>
            <a href={`tel:${CONTACT.phoneTel}`} data-testid="hero-call-btn" className="btn-ghost text-base px-8 py-4">
              <Phone size={18} /> {CONTACT.phoneDisplay}
            </a>
          </Reveal>

          <Reveal delay={0.8} className="mt-12 flex flex-wrap gap-8 text-sm text-white/60">
            {["Sans destruction", "Intervention rapide", "Compte-rendu pour l'assurance"].map((t) => (
              <span key={t} className="flex items-center gap-2">
                <span className="h-1.5 w-1.5 rounded-full bg-aqua" /> {t}
              </span>
            ))}
          </Reveal>
        </motion.div>
      </section>

      <BrandMarquee />

      {/* GOOGLE TRUST BAR */}
      <div className="bg-white">
        <div className="container-x -mt-8 relative z-20 flex justify-center">
          <GoogleTrustBar />
        </div>
      </div>

      {/* METHODS SUMMARY */}
      <section className="bg-white py-24 sm:py-32">
        <div className="container-x">
          <div className="grid lg:grid-cols-12 gap-12 items-end">
            <Reveal className="lg:col-span-7">
              <p className="overline text-aqua mb-4">Nos méthodes</p>
              <h2 className="text-4xl sm:text-5xl font-bold tracking-tight text-ink leading-tight">
                Une détection 100&nbsp;% non destructive
              </h2>
            </Reveal>
            <Reveal delay={0.15} className="lg:col-span-5 text-ink/70">
              Chaque situation a sa technologie. Nous combinons plusieurs instruments de précision
              pour localiser la fuite sans démolition inutile.
            </Reveal>
          </div>

          <div className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {METHODS.map((m, i) => (
              <Reveal key={m.title} delay={i * 0.06} className="group relative rounded-3xl border border-ink/10 bg-mist/40 p-7 hover:border-aqua/40 transition-colors">
                <span className="absolute right-6 top-5 text-6xl font-black text-aqua/10 select-none">{m.num}</span>
                <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-aqua/10 text-aqua">
                  <Icon name={m.icon} size={24} />
                </div>
                <h3 className="mt-5 text-xl font-bold text-ink">{m.title}</h3>
                <p className="mt-2.5 text-sm text-ink/65 leading-relaxed">{m.text}</p>
              </Reveal>
            ))}
            <Reveal delay={0.3} className="flex flex-col justify-center rounded-3xl bg-ink p-7">
              <p className="text-white/70 text-sm mb-4">Découvrez le détail de chaque technologie que nous utilisons.</p>
              <Link to="/methodes" data-testid="home-methods-link" className="inline-flex items-center gap-2 text-aqua font-bold">
                Voir nos méthodes <ArrowRight size={18} />
              </Link>
            </Reveal>
          </div>
        </div>
      </section>

      {/* SERVICES PREVIEW (bento) */}
      <section className="bg-mist py-24 sm:py-32">
        <div className="container-x">
          <Reveal>
            <p className="overline text-aqua mb-4">Nos services</p>
            <h2 className="text-4xl sm:text-5xl font-bold tracking-tight text-ink leading-tight max-w-2xl">
              Chaque fuite a sa solution
            </h2>
          </Reveal>
          <div className="mt-14 grid gap-5 md:grid-cols-12">
            {SERVICES.map((s, i) => (
              <Reveal
                key={s.slug}
                delay={i * 0.05}
                className={`rounded-3xl border border-ink/10 bg-white p-7 hover:border-aqua/40 transition-colors ${
                  i % 3 === 0 ? "md:col-span-6" : "md:col-span-3"
                }`}
              >
                <div className="flex items-center gap-3">
                  <Icon name={s.icon} size={22} className="text-aqua" />
                  <span className="text-xs font-bold tracking-wide text-ink/40">{s.num}</span>
                </div>
                <h3 className="mt-4 text-lg font-bold text-ink">{s.title}</h3>
                <p className="mt-2 text-sm text-ink/60 leading-relaxed line-clamp-3">{s.text}</p>
              </Reveal>
            ))}
          </div>
          <div className="mt-8">
            <Link to="/services" data-testid="home-services-link" className="btn-ghost">
              Tous nos services <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* AUDIENCES */}
      <section className="bg-white py-24 sm:py-32">
        <div className="container-x">
          <Reveal>
            <p className="overline text-aqua mb-4">Pour qui&nbsp;?</p>
            <h2 className="text-4xl sm:text-5xl font-bold tracking-tight text-ink leading-tight max-w-2xl">
              Nous intervenons pour tous les profils
            </h2>
          </Reveal>
          <div className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {AUDIENCES.map((a, i) => (
              <Reveal key={a.title} delay={i * 0.07} className="rounded-3xl border border-ink/10 p-7 hover:bg-ink hover:border-ink transition-colors group">
                <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-aqua/10 text-aqua group-hover:bg-aqua group-hover:text-white transition-colors">
                  <Icon name={a.icon} size={24} />
                </div>
                <h3 className="mt-5 text-lg font-bold text-ink group-hover:text-white transition-colors">{a.title}</h3>
                <p className="mt-2 text-sm text-ink/60 group-hover:text-white/70 transition-colors leading-relaxed">{a.text}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* BRAND / VÉHICULE */}
      <section className="bg-white py-24 sm:py-32">
        <div className="container-x grid lg:grid-cols-2 gap-14 items-center">
          <Reveal className="relative overflow-hidden rounded-3xl border border-ink/10 order-2 lg:order-1">
            <img src="/images/fuitex-van.jpg" alt="Véhicule FuiteX floqué stationné dans une rue de Paris" className="w-full h-auto object-cover" />
          </Reveal>
          <Reveal delay={0.1} className="order-1 lg:order-2">
            <p className="overline text-aqua mb-4">Une présence locale</p>
            <h2 className="text-4xl sm:text-5xl font-bold tracking-tight text-ink leading-tight">
              Une équipe équipée, sur le terrain près de chez vous
            </h2>
            <p className="mt-5 text-lg text-ink/70 max-w-md">
              Nos véhicules d'intervention sillonnent l'Île-de-France, prêts à localiser votre fuite
              avec un matériel de détection professionnel embarqué. Une vraie équipe, identifiable et de proximité.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              {["Matériel embarqué", "Intervention rapide", "Techniciens identifiables"].map((t) => (
                <span key={t} className="inline-flex items-center gap-2 rounded-full bg-mist px-4 py-2 text-sm font-semibold text-ink">
                  <span className="h-1.5 w-1.5 rounded-full bg-aqua" /> {t}
                </span>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* ZONE MAP */}
      <section className="bg-ink py-24 sm:py-32 relative overflow-hidden grain">
        <div className="container-x relative z-10 grid lg:grid-cols-2 gap-14 items-center">
          <Reveal>
            <p className="overline text-aqua mb-4">Zone d'intervention</p>
            <h2 className="text-4xl sm:text-5xl font-bold tracking-tight text-white leading-tight">
              Toute l'Île-de-France, 8 départements
            </h2>
            <p className="mt-5 text-white/70 max-w-md">
              De Paris à la grande couronne, nous couvrons l'ensemble de la région.
              Cliquez sur votre département pour découvrir notre intervention locale.
            </p>
            <Link to="/zone-intervention" data-testid="home-zone-link" className="mt-8 inline-flex btn-ghost">
              Explorer la zone <ArrowRight size={16} />
            </Link>
          </Reveal>
          <Reveal delay={0.15}>
            <IleDeFranceMap />
          </Reveal>
        </div>
      </section>

      {/* GOOGLE REVIEWS */}
      <GoogleReviews />

      {/* SEO LONG-FORM CONTENT */}
      <section className="bg-white py-24 sm:py-32">
        <div className="container-x">
          <LongForm data={SEO_CONTENT.home} />
        </div>
      </section>

      {/* SHORT FAQ + QUOTE FORM */}
      <section className="bg-white py-24 sm:py-32">
        <div className="container-x grid lg:grid-cols-2 gap-14 items-start">
          <div>
            <Reveal>
              <p className="overline text-aqua mb-4">Questions fréquentes</p>
              <h2 className="text-4xl sm:text-5xl font-bold tracking-tight text-ink leading-tight">
                Vos questions, nos réponses
              </h2>
            </Reveal>
            <div className="mt-10 space-y-6">
              {FAQ.slice(0, 3).map((f, i) => (
                <Reveal key={i} delay={i * 0.08} className="border-b border-ink/10 pb-6">
                  <h3 className="text-lg font-bold text-ink">{f.q}</h3>
                  <p className="mt-2 text-sm text-ink/65 leading-relaxed">{f.a}</p>
                </Reveal>
              ))}
              <Link to="/faq" data-testid="home-faq-link" className="inline-flex items-center gap-2 text-aqua font-bold">
                Toutes les questions <ArrowRight size={18} />
              </Link>
            </div>
          </div>
          <Reveal delay={0.15} id="devis">
            <QuoteForm />
          </Reveal>
        </div>
      </section>

      <CTASection />
    </>
  );
}
